import React, { useState, useEffect } from "react";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface GarageTypeControlProps {
  /** Starting garage type value */
  initialValue?: GarageType;
  /** Called with new valid value */
  onChange: (value: GarageType) => void;
}

export type GarageType = "Front" | "Side" | "Cottage" | "Split";

export const GARAGE_TYPE_OPTIONS: GarageType[] = [
  "Front",
  "Side",
  "Cottage",
  "Split",
];

/**
 * Themed select dropdown for "Garage Type".
 * Automatically adjusts to system light/dark mode.
 */
export const GarageTypeControl: React.FC<GarageTypeControlProps> = ({
  initialValue = "Front",
  onChange,
}) => {
  const theme = useSystemTheme();

  // Pull colors from current theme CSS variables
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const cardBg = css.getPropertyValue("--card-bg").trim();
  const inputBg =
    css.getPropertyValue("--input-bg").trim() || cardBg || "var(--card-bg)";
  const labelColor = css.getPropertyValue("--muted-text-color").trim() || textColor;
  const hoverBg =
    css.getPropertyValue("--hover-bg").trim() || "rgba(0,0,0,0.05)";

  const [value, setValue] = useState<GarageType>(initialValue);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (!GARAGE_TYPE_OPTIONS.includes(value)) {
      setError("Invalid garage type selection.");
    } else {
      setError("");
      onChange(value);
    }
  }, [value, onChange]);

  const handleMouseOver = (e: React.MouseEvent<HTMLSelectElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
    e.currentTarget.style.borderColor = borderColor;
  };

  const handleMouseOut = (e: React.MouseEvent<HTMLSelectElement>) => {
    e.currentTarget.style.backgroundColor = inputBg;
    e.currentTarget.style.borderColor = borderColor;
  };

  const selectClass =
    "w-full h-10 text-sm text-center rounded shadow-sm focus:outline-none cursor-pointer transition-all duration-150";
  const selectStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <Label htmlFor="garageTypeSelect" className={labelClass} style={labelStyle}>
        Garage Type
      </Label>

      <select
        id="garageTypeSelect"
        value={value}
        onChange={(e) => setValue(e.target.value as GarageType)}
        className={selectClass}
        style={selectStyle}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        {GARAGE_TYPE_OPTIONS.map((option) => (
          <option key={option} value={option} style={{ background: cardBg }}>
            {option}
          </option>
        ))}
      </select>

      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};